import {
    type ChapterLessonHash,
    type StreamFormValues,
    type SaveStreamRecord,
    type StreamChapter,
    type StreamRecord,
} from './StreamEditor.types';

/*
    toForm and fromForm are plain typed functions rather than createFormFieldConversions from
    FrontRoyalForm. That helper exists for useRecordFromAdminTable, which awaits an async conversion
    per field. This page has no async conversion, and the two derivations below need to see the whole
    record at once, which a per-field helper cannot express.

    Form field names match the server keys, so fromForm merges into the loaded record without a
    translation layer.
*/

export function toForm(record: StreamRecord): StreamFormValues {
    const entityMetadata = record.entity_metadata;
    const localePack = record.locale_pack;

    return {
        title: record.title || '',
        tag: record.tag || '',
        description: record.description || '',
        credits: record.credits || '',
        locale: record.locale,
        coming_soon: !!record.coming_soon,
        just_added: !!record.just_added,
        beta: !!record.beta,
        just_updated: !!record.just_updated,
        unlocked_for_mba_users: !!record.unlocked_for_mba_users,
        exam: !!record.exam,
        time_limit_hours: record.time_limit_hours ?? null,
        what_you_will_learn: (record.what_you_will_learn || []).map(value => ({ value })),
        recommended_stream_ids: (record.recommended_stream_ids || []).map(id => ({ id })),
        related_stream_ids: (record.related_stream_ids || []).map(id => ({ id })),
        resource_downloads: record.resource_downloads || [],
        resource_links: record.resource_links || [],
        summaries: record.summaries || [],
        chapters: record.chapters || [],
        requirement_identifier: localePack?.requirement_identifier || '',
        content_topics: localePack?.content_topics || [],
        seo_title: entityMetadata?.title || '',
        seo_description: entityMetadata?.description || '',
        seo_canonical_url: entityMetadata?.canonical_url || '',
        tweet_template: entityMetadata?.tweet_template || '',
    };
}

// The server rejects a chapter whose lesson_ids and lesson_hashes disagree, so lesson_ids is always
// derived here. The Angular page did this in an Iguana before-save callback.
export function chapterForSave(chapter: StreamChapter): StreamChapter {
    const lessonHashes: ChapterLessonHash[] = chapter.lesson_hashes || [];

    return {
        ...chapter,
        lesson_hashes: lessonHashes,
        lesson_ids: lessonHashes.map(lessonHash => lessonHash.lesson_id),
    };
}

// merge_hash clears and rebuilds the lessons join from this array, and reads only the id. The order
// sets each lesson's position, so it follows chapter order. A lesson used by two chapters appears
// once.
export function lessonsForSave(chapters: StreamChapter[]): { id: string }[] {
    const lessonIds = chapters.flatMap(chapter => (chapter.lesson_hashes || []).map(hash => hash.lesson_id));
    return [...new Set(lessonIds)].map(id => ({ id }));
}

export function fromForm(values: StreamFormValues, record: StreamRecord): SaveStreamRecord {
    const chapters = (values.chapters || []).map(chapterForSave);
    const localePack = record.locale_pack;

    return {
        // Every key the server reads must be present, and the loaded record is the only complete
        // source of them. Spreading it also carries through the fields this form does not edit.
        ...record,
        title: values.title,
        tag: values.tag,
        description: values.description,
        credits: values.credits,
        locale: values.locale,
        coming_soon: values.coming_soon,
        just_added: values.just_added,
        beta: values.beta,
        just_updated: values.just_updated,
        unlocked_for_mba_users: values.unlocked_for_mba_users,
        exam: values.exam,

        // The Iguana model dropped a time limit write unless exam was true, and nulled the value
        // when exam went false. The form disables the input instead, and this keeps the record
        // consistent for a course that was an exam and is not one now.
        time_limit_hours: values.exam ? values.time_limit_hours : null,

        what_you_will_learn: (values.what_you_will_learn || []).map(goal => goal.value),
        recommended_stream_ids: (values.recommended_stream_ids || []).map(reference => reference.id),
        related_stream_ids: (values.related_stream_ids || []).map(reference => reference.id),
        resource_downloads: values.resource_downloads || [],
        resource_links: values.resource_links || [],
        summaries: values.summaries || [],
        chapters,
        lessons: lessonsForSave(chapters),

        // handle_locale_pack reads content_items and detaches every translation the list leaves out,
        // so the pack is sent whole, with only the two fields this form edits replaced.
        //
        // A topic the admin typed has a client-generated id that matches no row yet.
        // ContentTopic.update_topics creates it from this hash, reading locales.en, which is why the
        // topics field only shows for the English course.
        locale_pack: localePack
            ? {
                  ...localePack,
                  requirement_identifier: values.requirement_identifier || null,
                  content_topics: values.content_topics,
              }
            : localePack,
    };
}
